// OSM `opening_hours` for the plan's warnings (FR-MAP-10), the same subset as PFStore's
// OpeningHours: `24/7`; rules split by `;`, each an optional weekday selector then times or
// `off`; later rules replace earlier ones. Anything else is unknown, and nothing is flagged.
type Range = { start: number; end: number };
export type HoursWarning =
  | { kind: 'closed'; weekday: number }
  | { kind: 'opens'; at: number }
  | { kind: 'closes'; at: number };

const NAMES: Record<string, number> = { Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6, Su: 7 };
export const DAY_NAMES = [
  '',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
  'Sundays',
];

function minutes(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 24 && min <= 59 ? h * 60 + min : null;
}

function weekdays(s: string): Set<number> | null {
  const out = new Set<number>();
  for (const piece of s.split(',')) {
    const [a, b, extra] = piece.split('-');
    const from = NAMES[a ?? ''];
    if (from == null || extra != null) return null;
    if (b == null) {
      out.add(from);
      continue;
    }
    const to = NAMES[b];
    if (to == null) return null;
    for (let d = from; ; d = (d % 7) + 1) {
      out.add(d);
      if (d === to) break;
    }
  }
  return out;
}

function times(s: string): Range[] | null {
  const out: Range[] = [];
  for (const piece of s.split(',').map((x) => x.trim())) {
    const [a, b, extra] = piece.split('-');
    const start = minutes(a ?? '');
    let end = minutes(b ?? '');
    if (start == null || end == null || extra != null) return null;
    if (end <= start) end += 1440;
    out.push({ start, end });
  }
  return out.length ? out : null;
}

export function parseHours(raw: string | null | undefined): Map<number, Range[]> | null {
  const text = raw?.trim();
  if (!text) return null;
  if (text === '24/7') return new Map([1, 2, 3, 4, 5, 6, 7].map((d) => [d, [{ start: 0, end: 1440 }]]));
  const days = new Map<number, Range[]>();
  for (const part of text
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean)) {
    const tokens = part.split(/\s+/);
    if (tokens[0]?.startsWith('PH')) {
      if (!tokens[0].includes(',')) continue;
      tokens[0] = tokens[0]
        .split(',')
        .filter((t) => t !== 'PH')
        .join(',');
    }
    let selected = new Set([1, 2, 3, 4, 5, 6, 7]);
    const first = tokens[0] ?? '';
    if (/^[A-Za-z]/.test(first) && !['off', 'closed'].includes(first.toLowerCase())) {
      const set = weekdays(first);
      if (!set) return null;
      selected = set;
      tokens.shift();
    }
    const rest = tokens.join(' ');
    if (!rest) return null;
    const ranges = ['off', 'closed'].includes(rest.toLowerCase()) ? [] : times(rest);
    if (!ranges) return null;
    for (const d of selected) days.set(d, ranges);
  }
  return days.size ? days : null;
}

/** 1 = Monday … 7 = Sunday. */
export const weekday = (date: string) => ((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;

export function hoursWarning(
  raw: string | null | undefined,
  date: string | null,
  start: string | null,
  duration: number | null,
): HoursWarning | null {
  const hours = parseHours(raw);
  if (!hours || !date) return null;
  const day = weekday(date);
  const ranges = hours.get(day);
  if (!ranges) return null;
  if (!ranges.length) return { kind: 'closed', weekday: day };
  const a = start ? minutes(start.slice(0, 5)) : null;
  if (a == null) return null;
  const b = a + (duration ?? 0);
  if (ranges.some((r) => r.start <= a && b <= r.end)) return null;
  const inside = ranges.find((r) => r.start <= a && a < r.end);
  if (inside) return { kind: 'closes', at: inside.end };
  const later = ranges.find((r) => r.start > a);
  if (later) return { kind: 'opens', at: later.start };
  return { kind: 'closes', at: Math.max(...ranges.map((r) => r.end)) };
}

export const clock = (m: number) =>
  `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
